/**
 * HR Service
 * Service for fetching and transforming HR summary data from BigDataAPI
 */

import { RawHRData, HRSummary, CompanySummary, DepartmentInfo, HRFilters, HRChartData } from '@/types/hr';
import { supabase } from '../supabase/client';

const CUSTOM_API_PROXY = '/api/hr-data';
const HR_API_BASE = 'http://103.69.193.159/BigDataAPI/api/HRBcc/Report_Summary_By_Dept';

/** Bearer header with the current Supabase session, required by the authenticated proxy. */
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch HR summary data from BigDataAPI
 */
export async function getHRData(filters: HRFilters): Promise<RawHRData[]> {
  const { fromDate, toDate, compIDs } = filters;
  
  // Strategy 1: Custom API Proxy (Primary)
  try {
    console.log('👥 HR Service: Trying custom API proxy...');
    const params = new URLSearchParams({
      fromDate,
      toDate,
      compIDs: compIDs.join(',')
    });
    
    const response = await fetch(`${CUSTOM_API_PROXY}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(await authHeader()),
      },
      signal: AbortSignal.timeout(60000), // 60s timeout for HR data
    });

    if (!response.ok) {
      throw new Error(`Custom API proxy error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('👥 HR Service: Custom API proxy success, data length:', Array.isArray(data) ? data.length : 'not array');
    return data;

  } catch (error) {
    // NOTE: third-party CORS proxy fallbacks (cors.sh) were removed — routing
    // internal HR data through external services leaked it. Use only our own
    // authenticated proxy.
    console.error('👥 HR Service: Custom API proxy failed:', error);
    throw new Error('Failed to fetch HR data via proxy');
  }
}

/**
 * Transform raw HR data to processed format
 */
export function transformHRData(rawData: RawHRData[]): HRSummary {
  if (!rawData || rawData.length === 0) {
    return {
      totalStaff: 0,
      present: 0,
      absent: 0,
      attendanceRate: 0,
      companies: []
    };
  }

  // Group data by company
  const companiesMap = new Map<number, RawHRData[]>();
  
  rawData.forEach(item => {
    if (!companiesMap.has(item.CompID)) {
      companiesMap.set(item.CompID, []);
    }
    companiesMap.get(item.CompID)!.push(item);
  });

  // Process each company
  const companies: CompanySummary[] = [];
  let totalStaff = 0;
  let totalPresent = 0;
  let totalAbsent = 0;

  companiesMap.forEach((companyData, compID) => {
    // Get company name from first item
    const compName = companyData[0]?.CompName || `Company ${compID}`;
    
    // Process departments
    const departments: DepartmentInfo[] = companyData.map(dept => ({
      deptCode: dept.DeptCode,
      deptName: dept.DeptCode, // You might want to map this to proper names
      totalStaff: dept.SiSo || 0,
      present: dept.DiLam || 0,
      absent: dept.Vang || 0,
      attendanceRate: dept.TiLeDiLam || 0
    }));

    // Calculate company totals
    const compTotalStaff = departments.reduce((sum, dept) => sum + dept.totalStaff, 0);
    const compPresent = departments.reduce((sum, dept) => sum + dept.present, 0);
    const compAbsent = departments.reduce((sum, dept) => sum + dept.absent, 0);
    const compAttendanceRate = compTotalStaff > 0 ? (compPresent / compTotalStaff) * 100 : 0;

    companies.push({
      compID,
      compName,
      totalStaff: compTotalStaff,
      present: compPresent,
      absent: compAbsent,
      attendanceRate: compAttendanceRate,
      departments: departments.sort((a, b) => a.deptCode.localeCompare(b.deptCode))
    });

    // Add to global totals
    totalStaff += compTotalStaff;
    totalPresent += compPresent;
    totalAbsent += compAbsent;
  });

  // Calculate overall attendance rate
  const overallAttendanceRate = totalStaff > 0 ? (totalPresent / totalStaff) * 100 : 0;

  return {
    totalStaff,
    present: totalPresent,
    absent: totalAbsent,
    attendanceRate: overallAttendanceRate,
    companies: companies.sort((a, b) => a.compName.localeCompare(b.compName))
  };
}

/**
 * Get chart data for HR dashboard
 */
export function getHRChartData(hrSummary: HRSummary): HRChartData[] {
  return hrSummary.companies.map(company => ({
    compName: company.compName,
    totalStaff: company.totalStaff,
    present: company.present,
    absent: company.absent,
    attendanceRate: company.attendanceRate
  }));
}

/**
 * Helper function to get department name mapping
 * You can expand this based on your department codes
 */
export function getDepartmentName(deptCode: string): string {
  const deptNameMap: Record<string, string> = {
    // Cushion factory (Xưởng nệm)
    'C_BOC XEP': 'Bóc xếp',
    'C_CAT MAY': 'Cắt may',
    'C_GON': 'Gòn',
    'C_HOAN THANH': 'Hoàn thành',
    'C_KCS': 'Kiểm chất sĩ',
    'C_KHO': 'Kho',
    'C_MAU NEM': 'Mẫu nệm',
    'C_MAY': 'May',
    'C_MUT': 'Mút',
    'C_PHU TRO': 'Phụ trợ',
    'C_TAI XE': 'Tài xế',
    'C_THONG KE': 'Thống kê',
    'C_THU KHO': 'Thủ kho',
    
    // Wood factory 1 (Xưởng gỗ 1)
    'G1_BAO BI': 'Bao bì',
    'G1_BOC XEP': 'Bóc xếp',
    'G1_CUA XE': 'Cưa xe',
    'G1_KCS': 'Kiểm chất sĩ',
    'G1_LAP RAP': 'Lắp ráp',
    'G1_LO SAY': 'Lò sấy',
    'G1_MASS': 'Mass',
    'G1_NGUOI': 'Nguội',
    'G1_PHU KIEM': 'Phụ kiểm',
    'G1_PHUN SON': 'Phun sơn',
    'G1_SO CHE': 'Sơ chế',
    'G1_SXC': 'SXC',
    'G1_TINH CHE': 'Tinh chế',
    
    // Wood factory 2 (Xưởng gỗ 2) 
    'G2_BAOBI': 'Bao bì',
    'G2_BOCXEP': 'Bóc xếp',
    'G2_CUA': 'Cưa',
    'G2_KCS': 'Kiểm chất sĩ',
    'G2_LAPRAP': 'Lắp ráp',
    'G2_LOSAY': 'Lò sấy',
    'G2_MASS': 'Mass',
    'G2_NGUOI': 'Nguội',
    'G2_PHUKIEM': 'Phụ kiểm',
    'G2_PHUNSON': 'Phun sơn',
    'G2_SOCHE': 'Sơ chế',
    'G2_SXC': 'SXC',
    'G2_TINHCHE': 'Tinh chế',
    
    // Metal factory (Xưởng sắt)
    'W_BEP': 'Bếp',
    'W_CO DIEN': 'Cơ điện',
    'W_HAN': 'Hàn',
    'W_KCS': 'Kiểm chất sĩ',
    'W_KHO': 'Kho',
    'W_MAU SAT': 'Mẫu sắt',
    'W_QUAN LY BP': 'Quản lý bộ phận',
    'W_SON': 'Sơn',
    'W_TAI XE': 'Tài xế',
    'W_THONG KE': 'Thống kê',
    'W_THU KHO': 'Thủ kho',
    'W_VE SINH SAT': 'Vệ sinh sắt',
    'W_XA PHOI': 'Xả phôi',
    
    // General departments (Tổng hợp)
    'BAO VE': 'Bảo vệ',
    'BGD': 'Ban giám đốc',
    'CHAT LUONG': 'Chất lượng',
    'IT': 'IT',
    'KE HOACH - GO 1': 'Kế hoạch - Gỗ 1',
    'KE HOACH - GO 2': 'Kế hoạch - Gỗ 2',
    'KE HOACH - NEM': 'Kế hoạch - Nệm',
    'KE HOACH - WICKER': 'Kế hoạch - Wicker',
    'KE TOAN': 'Kế toán',
    'KINH DOANH': 'Kinh doanh',
    'KY THUAT': 'Kỹ thuật',
    'KY THUAT - NEM': 'Kỹ thuật - Nệm',
    'LAI XE': 'Lái xe',
    'MUA HANG': 'Mua hàng',
    'NHAN SU': 'Nhân sự',
    'QL XUONG': 'Quản lý xưởng',
    'QUAN LY BP': 'Quản lý bộ phận'
  };
  
  return deptNameMap[deptCode] || deptCode;
}